import '@adornis/file-utils/client/x-image.js';
import { stateful, useState } from '@adornis/functional-lit/stateful.js';
import { useQuery } from '@adornis/functional-lit/use-query.js';
import { html, nothing } from 'lit';
import { repeat } from 'lit/directives/repeat.js';
import * as m from '../../../../localization/out/messages.js';
import { adminRouter } from './admin-routes.js';
import { subscribeAllCompanies } from '../../../api/company.js';

type CompanyStatusFilter = 'ACTIVE' | 'INACTIVE' | 'ALL';

export const AdminCompanyList = stateful(() => {
  const [statusFilter, setStatusFilter] = useState<CompanyStatusFilter>('ACTIVE');

  const companies = useQuery(
    () => subscribeAllCompanies()({ _id: 1, name: 1, logoID: 1, address: 1, sector: 1, website: 1, isActive: 1 }),
    [],
  );

  if (companies.loading) {
    return html`<div>${m.loading()}</div>`;
  }

  const filtered = (companies.value ?? []).filter(c => {
    if (statusFilter === 'ALL') return true;
    // Treat missing isActive as active (matches the $ne: false convention).
    const isActive = c.isActive !== false;
    return statusFilter === 'ACTIVE' ? isActive : !isActive;
  });

  const filterButton = (value: CompanyStatusFilter, label: string) => html`
    <button
      class="btn btn-sm join-item ${statusFilter === value ? 'btn-primary' : 'btn-ghost'}"
      @click=${() => setStatusFilter(value)}
    >
      ${label}
    </button>
  `;

  return html`
    <div>
      <div class="flex justify-between items-center mb-6">
        <h1 class="text-3xl font-bold text-base-content">${m.companies()}</h1>
        <button @click=${() => adminRouter.goTo('createCompany')} class="btn btn-primary">
          <span class="icon-[material-symbols--add]"></span>
          ${m.create_company()}
        </button>
      </div>

      <div class="flex items-end gap-3 mb-6">
        <div class="flex flex-col gap-1">
          <span class="text-sm text-base-content/70">${m.admin_company_list_filter_label()}</span>
          <div class="join">
            ${filterButton('ACTIVE', m.company_status_active())}
            ${filterButton('INACTIVE', m.company_status_inactive())}
            ${filterButton('ALL', m.admin_company_list_filter_all())}
          </div>
        </div>
      </div>

      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        ${repeat(
          filtered,
          company => company._id,
          company => html`
            <div
              class="card bg-base-100 shadow-xl flex flex-col transition-all duration-300 ease-in-out hover:shadow-2xl hover:-translate-y-1 overflow-hidden"
            >
              <div class="p-6 bg-base-100/10">
                <div class="flex items-center">
                  <div class="avatar ${company.logoID ? '' : 'placeholder'} mr-6">
                    <div
                      class="bg-primary text-primary-content rounded-full w-16 h-16 ring-4 ring-primary/20 overflow-hidden"
                    >
                      ${
                        company.logoID
                          ? html`<x-image
                              class="h-full w-full"
                              .value=${company.logoID}
                              mode="cover"
                              alt="${company.name || 'Company logo'}"
                            ></x-image>`
                          : html`<span class="text-3xl font-bold">${company.name?.charAt(0)}</span>`
                      }
                    </div>
                  </div>
                  <div class="pl-2">
                    <div class="flex items-center gap-2 flex-wrap">
                      <h2 class="card-title text-xl font-bold text-base-content">${company.name}</h2>
                      ${
                        company.isActive === false
                          ? html`<span class="badge badge-ghost badge-sm">${m.company_status_inactive()}</span>`
                          : nothing
                      }
                    </div>
                    <p class="text-base-content/70">${company.sector}</p>
                  </div>
                </div>
              </div>
              <div class="card-body pt-4 flex-grow">
                <div class="space-y-4 text-base-content">
                  <div class="flex items-start">
                    <span class="mr-3 mt-1 text-primary/70 icon-[material-symbols--location-on]"></span>
                    <div>
                      <div class="font-semibold text-sm text-base-content/70">${m.address()}</div>
                      <div class="text-base-content">${company.address}</div>
                    </div>
                  </div>
                  <div class="flex items-start">
                    <span class="mr-3 mt-1 text-primary/70 icon-[material-symbols--language]"></span>
                    <div>
                      <div class="font-semibold text-sm text-base-content/70">${m.company_profile_website_label()}</div>
                      <a href="${company.website}" target="_blank" class="link link-primary">${company.website}</a>
                    </div>
                  </div>
                </div>
              </div>
              <div class="card-actions p-4 mt-auto">
                <button
                  @click=${(e: Event) => {
                    e.preventDefault();
                    adminRouter.goTo('companyDetail', { params: { companyID: company._id! } });
                  }}
                  class="btn btn-primary w-full"
                >
                  ${m.view_details()}
                  <span class="icon-[material-symbols--arrow-forward]"></span>
                </button>
              </div>
            </div>
          `,
        )}
      </div>
    </div>
  `;
});
